import { Component, forwardRef, input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ControlValueAccessor, Validator, AbstractControl, ValidationErrors } from '@angular/forms';

import { Option, Selects } from './models';
import DateUtils from './date-utils';

export const DEFAULT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true,
};

export const DEFAULT_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true,
};

const noop = () => {};

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  providers: [DEFAULT_VALUE_ACCESSOR, DEFAULT_VALIDATOR],
})
export class DatePickerComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  date = input<Date | string | number | null>(null);
  minDate = input<Date | string | number | null>(null);
  maxDate = input<Date | string | number | null>(null);
  months = input<string[] | string | null>(null);
  order = input<string>('dmy');
  attrsDate = input<Record<string, unknown>>({});
  attrsMonth = input<Record<string, unknown>>({});
  attrsYear = input<Record<string, unknown>>({});
  yearOrder = input<string>('');
  timezone = input<number>(0);
  placeholder = input<string[] | string | null>(null);
  ngRequired = input<boolean>(false);
  disabled = input<boolean | boolean[]>(false);
  visible = input<boolean | boolean[]>([true, true, true]);
  reverseYear = input<boolean>(false);

  private _onTouchedCallback: (_: any) => void = noop;

  selects: Selects = {
    d: { visible: true, value: null, options: [], attrs: {}, disabled: false },
    m: { visible: true, value: null, options: [], attrs: {}, disabled: false },
    y: { visible: true, value: null, options: [], attrs: {}, disabled: false },
  };

  private monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  private _minDate: Date = new Date();
  private _maxDate: Date = new Date();
  private placeHolders: Option[] | null = null;
  private ngModel: Date | null = null;
  orderArray: string[] = [];

  ngOnInit() {
    this.ngModel = DateUtils.parseDate(this.ngModel, this.timezone());

    this.selects.d.attrs = this.attrsDate();
    this.selects.m.attrs = this.attrsMonth();
    this.selects.y.attrs = this.attrsYear();

    const disabledVal = this.disabled();
    if (typeof disabledVal === 'boolean') {
      this.selects.d.disabled = disabledVal;
      this.selects.m.disabled = disabledVal;
      this.selects.y.disabled = disabledVal;
    } else if (Array.isArray(disabledVal) && disabledVal.length === 3) {
      this.selects.d.disabled = disabledVal[0];
      this.selects.m.disabled = disabledVal[1];
      this.selects.y.disabled = disabledVal[2];
    }

    const visibleVal = this.visible();
    if (typeof visibleVal === 'boolean') {
      this.selects.d.visible = visibleVal;
      this.selects.m.visible = visibleVal;
      this.selects.y.visible = visibleVal;
    } else if (Array.isArray(visibleVal) && visibleVal.length === 3) {
      this.selects.d.visible = visibleVal[0];
      this.selects.m.visible = visibleVal[1];
      this.selects.y.visible = visibleVal[2];
    }

    const initDate = DateUtils.parseDate(this.date(), this.timezone());
    if (initDate != null) {
      this.ngModel = initDate;
    }

    const orderStr = this.order();
    if (typeof orderStr !== 'string') {
      this.orderArray = 'dmy'.split('');
    } else {
      this.orderArray = orderStr.toLowerCase().split('');
    }

    this._minDate = DateUtils.parseDate(this.minDate(), this.timezone()) || new Date(new Date().getFullYear() - 100, new Date().getMonth(), new Date().getDate());
    this._maxDate = DateUtils.parseDate(this.maxDate(), this.timezone()) || new Date();

    if (this.ngModel && this.ngModel < this._minDate) {
      this.ngModel = this._minDate;
    }
    if (this.ngModel && this.ngModel > this._maxDate) {
      this.ngModel = this._maxDate;
    }

    const monthsVal = this.months();
    if (monthsVal !== undefined && monthsVal !== null) {
      if (typeof monthsVal === 'string') {
        const months = monthsVal.split(',');
        if (months.length === 12) {
          this.monthNames = months;
        }
      }
      if (Array.isArray(monthsVal) && monthsVal.length === 12) {
        this.monthNames = monthsVal;
      }
    }

    this.updatePlaceholders();
    this.updateMonthList(null);
    this.updateYearList(null);
    this.updateDateList(null, null);
    this.writeValue(this.ngModel);
  }

  onTouched() {
    this._onTouchedCallback(null);
  }

  onChange() {
    let res: Date | null = null;

    if (this.selects.d.value && !isNaN(this.selects.m.value as number)) {
      const maxDay = DateUtils.getMaxDate((this.selects.m.value as number) + 1, this.selects.y.value as number);

      let hours = 12;
      let minutes = 0;
      let seconds = 0;
      let milliseconds = 0;
      if (this.ngModel != null) {
        hours = this.ngModel.getHours();
        minutes = this.ngModel.getMinutes();
        seconds = this.ngModel.getSeconds();
        milliseconds = this.ngModel.getMilliseconds();
      }

      if ((this.selects.d.value as number) > maxDay) {
        this.selects.d.value = maxDay;
      }

      res = new Date(Date.UTC(this.selects.y.value as number, this.selects.m.value as number, this.selects.d.value as number, hours, minutes, seconds, milliseconds));

      res = DateUtils.parseDate(res, this.timezone());
    }

    if (this.placeHolders) {
      if (this.selects.y.value) {
        this.placeHolders[0].disabled = true;
      }
      if (this.selects.m.value !== null) {
        this.placeHolders[1].disabled = true;
      }
      if (this.selects.d.value) {
        this.placeHolders[2].disabled = true;
      }
    }

    this.updateMonthList(this.selects.y.value);
    this.updateYearList(this.selects.m.value);
    this.updateDateList(this.selects.m.value, this.selects.y.value);

    this.propagateChange(res);
  }

  propagateChange: any = () => {};

  writeValue(value: Date | null): void {
    if (value) {
      this.selects.d.value = value.getDate();
      this.selects.m.value = value.getMonth();
      this.selects.y.value = value.getFullYear();
    } else {
      const visibleVal = this.visible();
      if (typeof visibleVal === 'boolean') {
        this.selects.d.value = visibleVal ? null : 1;
        this.selects.m.value = visibleVal ? null : 0;
        this.selects.y.value = visibleVal ? null : new Date().getFullYear();
      } else if (Array.isArray(visibleVal) && visibleVal.length === 3) {
        this.selects.d.value = visibleVal[0] ? null : 1;
        this.selects.m.value = visibleVal[1] ? null : 0;
        this.selects.y.value = visibleVal[2] ? null : new Date().getFullYear();
      }

      if (this.placeHolders) {
        this.placeHolders[0].disabled = false;
        this.placeHolders[1].disabled = false;
        this.placeHolders[2].disabled = false;
      }
    }

    this.updateMonthList(this.selects.y.value);
    this.updateYearList(this.selects.m.value);
    this.updateDateList(this.selects.m.value, this.selects.y.value);
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.selects.d.value && DateUtils.parseIntStrict(this.selects.m.value as number) !== null && this.selects.y.value ? null : { required: true };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['placeholder'] && !changes['placeholder'].isFirstChange()) {
      this.updatePlaceholders();
      this.updateMonthList(this.selects.y.value);
      this.updateYearList(this.selects.m.value);
      this.updateDateList(this.selects.m.value, this.selects.y.value);
    }
  }

  updateMonthList(year: number | null) {
    year = DateUtils.parseIntStrict(year);

    const start = year !== null && year === this._minDate.getFullYear() ? this._minDate.getMonth() : 0;
    const end = year !== null && year === this._maxDate.getFullYear() ? this._maxDate.getMonth() : 11;

    this.selects.m.options = [];
    if (this.placeHolders) {
      this.selects.m.options.push(this.placeHolders[1]);
    }
    for (let i = start; i <= end; i++) {
      this.selects.m.options.push({ value: i, name: this.monthNames[i], disabled: false });
    }
  }

  updateYearList(month: number | null) {
    month = DateUtils.parseIntStrict(month);

    this.selects.y.options = [];
    const isReverse = this.reverseYear();
    for (let i = this._minDate.getFullYear(); i <= this._maxDate.getFullYear(); i++) {
      const now = new Date();
      if (month !== null && month + 1 < this._minDate.getMonth() && now.getFullYear() === i && ((isReverse && month > now.getMonth()) || (!isReverse && month < now.getMonth()))) {
        continue;
      }

      this.selects.y.options.push({ value: i, name: i.toString(), disabled: false });
    }

    if (isReverse) {
      this.selects.y.options.reverse();
    }

    if (this.placeHolders) {
      this.selects.y.options.unshift(this.placeHolders[0]);
    }
  }

  updateDateList(month: number | null, year: number | null) {
    month = DateUtils.parseIntStrict(month);
    year = DateUtils.parseIntStrict(year);

    let start = 1;
    if (month !== null && month === this._minDate.getMonth() && year !== null && year === this._minDate.getFullYear()) {
      start = this._minDate.getDate();
    }

    let end = DateUtils.getMaxDate(month !== null ? month + 1 : null, year);
    if (month !== null && month === this._maxDate.getMonth() && year !== null && year === this._maxDate.getFullYear()) {
      end = this._maxDate.getDate();
    }

    this.selects.d.options = [];
    if (this.placeHolders) {
      this.selects.d.options.push(this.placeHolders[2]);
    }
    for (let i = start; i <= end; i++) {
      this.selects.d.options.push({
        value: i,
        name: i.toString(),
        disabled: false,
      });
    }
  }

  updatePlaceholders() {
    this.placeHolders = null;
    const placeholderVal = this.placeholder();
    if (placeholderVal !== undefined && placeholderVal !== null && (typeof placeholderVal === 'string' || Array.isArray(placeholderVal))) {
      const holders = typeof placeholderVal === 'string' ? placeholderVal.split(',') : placeholderVal;
      if (holders.length === 3) {
        this.placeHolders = [];
        for (const h of holders) {
          this.placeHolders.push({ value: null, name: h, disabled: false });
        }
      }
    }
  }
}
